import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import { erpIntegrationService } from '../../services/erpIntegrationService';
import { payrollIntegrationService } from '../../services/payrollIntegrationService';
import SyncOverviewDashboard from './components/SyncOverviewDashboard';
import EmployeeSyncTable from './components/EmployeeSyncTable';
import PayrollMappingEngine from './components/PayrollMappingEngine';
import ValidationPanel from './components/ValidationPanel';

const PayrollSystemSynchronizationCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [syncSessions, setSyncSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [erpConnections, integrationSessions] = await Promise.all([
        erpIntegrationService?.getAllConnections(),
        payrollIntegrationService?.getAllIntegrationSessions()
      ]);
      setConnections(erpConnections?.filter(c => c?.connectionStatus === 'connected'));
      setSyncSessions(integrationSessions);
      if (erpConnections?.length > 0) {
        setSelectedConnection(erpConnections?.[0]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Sync Overview', icon: 'BarChart3' },
    { id: 'employees', label: 'Employee Sync', icon: 'Users' },
    { id: 'mapping', label: 'Payroll Mapping', icon: 'GitBranch' },
    { id: 'validation', label: 'Validation', icon: 'CheckCircle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Payroll System Synchronization Center</h1>
              <p className="text-muted-foreground mt-1">
                Automated bi-directional data exchange with PNG Employment Act compliance validation
              </p>
            </div>
            <button
              onClick={() => navigate('/erp-integration-hub')}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Icon name="Settings" size={16} />
              ERP Settings
            </button>
          </div>
        </div>

        {/* Connection Selector */}
        {connections?.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Active ERP Connection
            </label>
            <select
              value={selectedConnection?.id || ''}
              onChange={(e) => {
                const conn = connections?.find(c => c?.id === e?.target?.value);
                setSelectedConnection(conn);
              }}
              className="w-full md:w-96 px-4 py-2 bg-background border border-border rounded-lg text-foreground"
            >
              {connections?.map((conn) => (
                <option key={conn?.id} value={conn?.id}>
                  {erpIntegrationService?.getERPSystemLabel(conn?.erpSystem)} - {conn?.connectionName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-card border border-border rounded-lg">
          {/* Tabs */}
          <div className="border-b border-border">
            <div className="flex items-center gap-1 p-1">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-all ${
                    activeTab === tab?.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading synchronization data...</p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && (
                  <SyncOverviewDashboard
                    connection={selectedConnection}
                    syncSessions={syncSessions}
                  />
                )}

                {activeTab === 'employees' && selectedConnection && (
                  <EmployeeSyncTable
                    connection={selectedConnection}
                  />
                )}

                {activeTab === 'mapping' && selectedConnection && (
                  <PayrollMappingEngine
                    connection={selectedConnection}
                  />
                )}

                {activeTab === 'validation' && selectedConnection && (
                  <ValidationPanel
                    connection={selectedConnection}
                  />
                )}

                {!selectedConnection && (
                  <div className="text-center py-12">
                    <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No active ERP connections found</p>
                    <button
                      onClick={() => navigate('/erp-integration-hub')}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Configure ERP Connection
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollSystemSynchronizationCenter;
